import type { Attribution } from '@researcher/shared';

const ATTR_KEY = 'romy_attribution';
const ANON_KEY = 'romy_anon_id';

/** Persistent anonymous id, lets us join pre-signup events to a user later. */
export function getAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    // Storage unavailable (private mode / SSR) — return an ephemeral id.
    return 'anon';
  }
}

/**
 * Captures first-touch acquisition attribution (UTM params + referrer) once and
 * stores it. First-touch wins: we never overwrite an existing record, so the
 * original source survives later internal navigation.
 */
export function captureAttribution(): void {
  try {
    if (localStorage.getItem(ATTR_KEY)) return; // first touch already recorded

    const params = new URLSearchParams(window.location.search);
    const get = (k: string) => {
      const v = params.get(k);
      return v ? v.slice(0, 256) : undefined;
    };

    let referrerHost: string | undefined;
    try {
      if (document.referrer) {
        const host = new URL(document.referrer).hostname;
        // Ignore self-referrals (internal navigation)
        if (host && host !== window.location.hostname) referrerHost = host;
      }
    } catch {
      /* malformed referrer — ignore */
    }

    const attr: Attribution = {
      utmSource: get('utm_source'),
      utmMedium: get('utm_medium'),
      utmCampaign: get('utm_campaign'),
      utmTerm: get('utm_term'),
      utmContent: get('utm_content'),
      referrerHost,
      landingPath: window.location.pathname.slice(0, 256),
      firstTouchAt: new Date().toISOString(),
      anonId: getAnonId(),
    };

    // Only persist if we actually learned something beyond the bare anon id.
    const meaningful =
      attr.utmSource ||
      attr.utmMedium ||
      attr.utmCampaign ||
      attr.referrerHost ||
      attr.landingPath;
    if (meaningful) {
      localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    }
  } catch {
    /* storage unavailable — skip */
  }
}

/** Reads the stored first-touch attribution (with a fresh anonId fallback). */
export function getAttribution(): Attribution {
  try {
    const raw = localStorage.getItem(ATTR_KEY);
    const stored: Attribution = raw ? JSON.parse(raw) : {};
    return { ...stored, anonId: stored.anonId || getAnonId() };
  } catch {
    return { anonId: getAnonId() };
  }
}
